import React, { useState, useEffect } from 'react';
import { Cloud, Code, CheckCircle2, AlertCircle } from 'lucide-react';
import './A2AServerToggle.css';

const A2AServerToggle = () => {
  // Detect if we're on Cloudflare/production
  const isProduction = typeof window !== 'undefined' && 
                       window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1' &&
                       !window.location.hostname.includes('localhost');
  
  const [useLocalhost, setUseLocalhost] = useState(() => {
    // If on production, force production mode (ignore localStorage)
    if (isProduction) {
      // Clear any localhost preference from localStorage
      localStorage.setItem('a2a_use_localhost', 'false');
      return false;
    }
    
    // For localhost, load from localStorage, default to false (production)
    const saved = localStorage.getItem('a2a_use_localhost');
    return saved === 'true';
  });

  const [isAnimating, setIsAnimating] = useState(false);

  // Set initial URL immediately on mount (before any A2A calls)
  useEffect(() => {
    // Determine the correct server URL
    const serverUrl = isProduction 
      ? 'https://a2aenglishai-server.appsokay.net'
      : (useLocalhost ? 'http://localhost:8787' : 'https://a2aenglishai-server.appsokay.net');
    
    // Update the A2A client URL immediately
    if (window.updateA2AServerUrl) {
      window.updateA2AServerUrl(serverUrl);
      console.log(`[A2A Server Toggle] Initial URL set to: ${serverUrl} (isProduction: ${isProduction}, useLocalhost: ${useLocalhost})`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount
  
  // Update URL when toggle state changes
  useEffect(() => {
    // If on production, always use production URL (don't allow localhost)
    if (isProduction) {
      const serverUrl = 'https://a2aenglishai-server.appsokay.net';
      if (window.updateA2AServerUrl) {
        window.updateA2AServerUrl(serverUrl);
        console.log(`[A2A Server Toggle] Production mode: Using Cloudflare server`);
      }
      // Force state to false
      if (useLocalhost) {
        setUseLocalhost(false);
      }
      return;
    }
    
    // For localhost, save preference and update URL
    localStorage.setItem('a2a_use_localhost', useLocalhost.toString());
    
    // Update server URL
    const serverUrl = useLocalhost 
      ? 'http://localhost:8787'
      : 'https://a2aenglishai-server.appsokay.net';
    
    // Update the A2A client URL immediately
    if (window.updateA2AServerUrl) {
      window.updateA2AServerUrl(serverUrl);
      console.log(`[A2A Server Toggle] Switched to: ${useLocalhost ? 'localhost:8787' : 'Cloudflare production server'}`);
    }
  }, [useLocalhost, isProduction]);

  const handleToggle = () => {
    if (isProduction) return; // Disabled on production
    
    setIsAnimating(true);
    setUseLocalhost(!useLocalhost);
    
    // Reset animation state after transition
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const currentServer = useLocalhost ? 'Localhost' : 'Cloudflare';
  const currentUrl = useLocalhost ? 'localhost:8787' : 'Production';
  const isActive = !useLocalhost; // Cloudflare is the "active" production state

  return (
    <div className="a2a-server-toggle-container">
      <div className={`a2a-server-toggle-card ${isProduction ? 'production-mode' : ''} ${isAnimating ? 'animating' : ''}`}>
        {/* Status Indicator */}
        <div className={`a2a-status-indicator ${isActive ? 'active' : 'inactive'}`}>
          {isActive ? (
            <CheckCircle2 className="a2a-status-icon" />
          ) : (
            <AlertCircle className="a2a-status-icon" />
          )}
        </div>

        {/* Toggle Button */}
        <button
          onClick={handleToggle}
          disabled={isProduction}
          className={`a2a-toggle-button ${useLocalhost ? 'localhost' : 'cloudflare'} ${isProduction ? 'disabled' : ''}`}
          aria-label={`Switch to ${useLocalhost ? 'Cloudflare' : 'Localhost'} server`}
        >
          <div className="a2a-toggle-track">
            <div className={`a2a-toggle-slider ${useLocalhost ? 'slider-left' : 'slider-right'}`}>
              {useLocalhost ? (
                <Code className="a2a-toggle-icon" />
              ) : (
                <Cloud className="a2a-toggle-icon" />
              )}
            </div>
          </div>
        </button>

        {/* Server Info */}
        <div className="a2a-server-info">
          <div className="a2a-server-label">
            <span className="a2a-server-name">{currentServer}</span>
            {isProduction && (
              <span className="a2a-production-badge">Production</span>
            )}
          </div>
          <span className="a2a-server-url">{currentUrl}</span>
        </div>
      </div>
    </div>
  );
};

export default A2AServerToggle;

